import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2, Check, Sun, Moon, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function VendeurSettings() {
  const { isDark, toggleTheme } = useOutletContext() || {};
  const photoInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  // Profile
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [profilePhoto, setProfilePhoto] = useState("");

  // Password
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwdError, setPwdError] = useState("");

  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const accent = boutiqueData.customColor || "#FF4500";

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await axios.get(`${API}/team/me/profile`, getAuthHeaders());
        const d = res.data;
        setPrenom(d.prenom || "");
        setNom(d.nom || "");
        setEmail(d.email || "");
        setRole(d.role || "vendeur");
        setProfilePhoto(d.profile_photo || "");
      } catch {
        toast.error("Erreur lors du chargement du profil");
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    if (!prenom.trim()) { toast.error("Le prenom est obligatoire"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/team/me/profile`, { prenom, nom, profile_photo: profilePhoto }, getAuthHeaders());
      // Update sessionStorage
      const cu = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
      cu.prenom = prenom;
      sessionStorage.setItem("currentUser", JSON.stringify(cu));
      toast.success("Profil mis a jour");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("L'image ne doit pas depasser 2 Mo"); return; }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      try {
        await axios.put(`${API}/team/me/profile`, { profile_photo: base64 }, getAuthHeaders());
        setProfilePhoto(base64);
        toast.success("Photo mise a jour");
      } catch { toast.error("Erreur lors de l'upload"); }
    };
    reader.readAsDataURL(file);
  };

  const handleChangePassword = async () => {
    setPwdError("");
    if (!oldPassword) { setPwdError("Entrez votre ancien mot de passe"); return; }
    if (newPassword.length < 8) { setPwdError("Le nouveau mot de passe doit contenir au moins 8 caracteres"); return; }
    if (newPassword !== confirmPassword) { setPwdError("Les mots de passe ne correspondent pas"); return; }

    setSavingPwd(true);
    try {
      await axios.put(`${API}/team/me/password`, { old_password: oldPassword, new_password: newPassword }, getAuthHeaders());
      toast.success("Mot de passe modifie avec succes");
      setOldPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur";
      setPwdError(detail);
    } finally {
      setSavingPwd(false);
    }
  };

  const getInitials = (p, n) => {
    const f = (p || "").charAt(0).toUpperCase();
    const l = (n || "").charAt(0).toUpperCase();
    return f + l || "?";
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#6B7280' }} />
    </div>
  );

  return (
    <div className="vs-page" data-testid="vendeur-settings-page">
      {/* Header */}
      <div className="vs-header">
        <h1 className="vs-title">Mon profil</h1>
        <p className="vs-subtitle">Gerez vos informations personnelles</p>
      </div>

      {/* Photo + Identity */}
      <div className="vs-card" data-testid="vendeur-profile-card">
        <div className="vs-photo-section">
          <div className="vs-avatar-wrapper" onClick={() => photoInputRef.current?.click()} data-testid="vendeur-photo-upload">
            <div className="vs-avatar" style={{ background: profilePhoto ? 'transparent' : `linear-gradient(135deg, ${accent}, ${accent}88)` }}>
              {profilePhoto ? (
                <img src={profilePhoto} alt="Photo" className="vs-avatar-img" />
              ) : (
                <span className="vs-avatar-initials">{getInitials(prenom, nom)}</span>
              )}
              <div className="vs-avatar-overlay">
                <Camera size={18} />
              </div>
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={(e) => handlePhotoUpload(e.target.files[0])} />
          </div>
          <div className="vs-identity">
            <span className="vs-display-name">{prenom} {nom}</span>
            <span className="vs-role-badge" style={{ background: `${accent}18`, color: accent }}>{role}</span>
            <span className="vs-email">{email}</span>
          </div>
        </div>
      </div>

      {/* Informations */}
      <div className="vs-card" data-testid="vendeur-info-section">
        <h2 className="vs-card-title">Informations personnelles</h2>
        <div className="vs-form-grid">
          <div>
            <label className="vs-label">Prenom *</label>
            <input className="vs-input" value={prenom} onChange={(e) => setPrenom(e.target.value)} data-testid="vendeur-prenom-input" />
          </div>
          <div>
            <label className="vs-label">Nom</label>
            <input className="vs-input" value={nom} onChange={(e) => setNom(e.target.value)} data-testid="vendeur-nom-input" />
          </div>
          <div className="vs-form-full">
            <label className="vs-label">Email</label>
            <input className="vs-input vs-input-disabled" value={email} disabled data-testid="vendeur-email-input" />
            <span className="vs-hint">L'email est gere par le proprietaire</span>
          </div>
        </div>
        <button className="vs-save-btn" onClick={handleSaveProfile} disabled={saving} data-testid="vendeur-save-profile-btn" style={{ background: accent }}>
          {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={16} />}
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>
      </div>

      {/* Password */}
      <div className="vs-card" data-testid="vendeur-password-section">
        <h2 className="vs-card-title">Changer le mot de passe</h2>
        <div className="vs-form-stack">
          <div>
            <label className="vs-label">Ancien mot de passe</label>
            <div className="vs-input-wrapper">
              <input className="vs-input" type={showOld ? "text" : "password"} value={oldPassword} onChange={(e) => { setOldPassword(e.target.value); setPwdError(""); }} data-testid="vendeur-old-password" />
              <button className="vs-eye-btn" onClick={() => setShowOld(v => !v)} type="button">{showOld ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div>
            <label className="vs-label">Nouveau mot de passe</label>
            <div className="vs-input-wrapper">
              <input className="vs-input" type={showNew ? "text" : "password"} value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setPwdError(""); }} data-testid="vendeur-new-password" />
              <button className="vs-eye-btn" onClick={() => setShowNew(v => !v)} type="button">{showNew ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <div>
            <label className="vs-label">Confirmer le mot de passe</label>
            <input className="vs-input" type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setPwdError(""); }} data-testid="vendeur-confirm-password" />
          </div>
          {pwdError && <div className="vs-error" data-testid="vendeur-pwd-error">{pwdError}</div>}
          <button className="vs-save-btn vs-save-outline" onClick={handleChangePassword} disabled={savingPwd} data-testid="vendeur-change-pwd-btn">
            {savingPwd ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
            {savingPwd ? "Modification..." : "Modifier le mot de passe"}
          </button>
        </div>
      </div>

      {/* Theme */}
      <div className="vs-card" data-testid="vendeur-theme-section">
        <h2 className="vs-card-title">Apparence</h2>
        <div className="vs-theme-row">
          <div className="vs-theme-info">
            {isDark ? <Moon size={18} style={{ color: '#8B5CF6' }} /> : <Sun size={18} style={{ color: '#F59E0B' }} />}
            <div>
              <span className="vs-theme-label">{isDark ? "Mode sombre" : "Mode clair"}</span>
              <span className="vs-theme-desc">Changez l'apparence du tableau de bord</span>
            </div>
          </div>
          <button className={`vs-toggle ${isDark ? 'on' : ''}`} onClick={toggleTheme} data-testid="vendeur-theme-toggle">
            <span className="vs-toggle-knob" />
          </button>
        </div>
      </div>

      <style>{`
        .vs-page { max-width: 640px; margin: 0 auto; display: flex; flex-direction: column; gap: 20px; padding-bottom: 40px; }
        .vs-header { margin-bottom: 4px; }
        .vs-title { font-size: 28px; font-weight: 700; color: ${isDark ? '#fff' : '#111'}; margin: 0; }
        .vs-subtitle { color: ${isDark ? '#6B7280' : '#9CA3AF'}; font-size: 14px; margin-top: 4px; }

        .vs-card {
          background: ${isDark ? '#1A1A1A' : '#ffffff'};
          border: 1px solid ${isDark ? '#2A2A2A' : '#E5E7EB'};
          border-radius: 16px; padding: 24px;
        }
        .vs-card-title { font-size: 16px; font-weight: 600; color: ${isDark ? '#fff' : '#111'}; margin: 0 0 16px; }

        /* Photo section */
        .vs-photo-section { display: flex; align-items: center; gap: 20px; }
        .vs-avatar-wrapper { cursor: pointer; flex-shrink: 0; }
        .vs-avatar {
          width: 80px; height: 80px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          position: relative; overflow: hidden;
        }
        .vs-avatar-img { width: 100%; height: 100%; object-fit: cover; }
        .vs-avatar-initials { color: #fff; font-size: 28px; font-weight: 700; }
        .vs-avatar-overlay {
          position: absolute; inset: 0; background: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          color: #fff; opacity: 0; transition: opacity 0.2s;
        }
        .vs-avatar-wrapper:hover .vs-avatar-overlay { opacity: 1; }
        .vs-identity { display: flex; flex-direction: column; gap: 4px; }
        .vs-display-name { font-size: 20px; font-weight: 700; color: ${isDark ? '#fff' : '#111'}; }
        .vs-role-badge {
          display: inline-block; width: fit-content;
          border-radius: 20px; font-size: 11px; font-weight: 600;
          padding: 2px 10px; text-transform: capitalize;
        }
        .vs-email { color: ${isDark ? '#6B7280' : '#9CA3AF'}; font-size: 13px; }

        /* Form */
        .vs-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .vs-form-full { grid-column: 1 / -1; }
        .vs-form-stack { display: flex; flex-direction: column; gap: 16px; }
        .vs-label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: ${isDark ? '#6B7280' : '#9CA3AF'}; margin-bottom: 6px; font-weight: 500; }
        .vs-input {
          width: 100%; background: ${isDark ? '#0F0F0F' : '#F9FAFB'};
          border: 1px solid ${isDark ? '#333' : '#D1D5DB'}; border-radius: 10px;
          padding: 10px 14px; color: ${isDark ? '#fff' : '#111'}; font-size: 14px;
          font-family: inherit; transition: border-color 0.2s; box-sizing: border-box;
        }
        .vs-input:focus { outline: none; border-color: ${accent}; }
        .vs-input-disabled { opacity: 0.5; cursor: not-allowed; }
        .vs-hint { font-size: 11px; color: ${isDark ? '#4B5563' : '#9CA3AF'}; margin-top: 4px; display: block; }

        .vs-input-wrapper { position: relative; }
        .vs-eye-btn {
          position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          background: none; border: none; color: ${isDark ? '#6B7280' : '#9CA3AF'};
          cursor: pointer; padding: 2px;
        }

        .vs-error { color: #FCA5A5; font-size: 13px; }

        /* Buttons */
        .vs-save-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; margin-top: 16px; padding: 12px;
          color: #fff; border: none; border-radius: 10px;
          font-size: 14px; font-weight: 600; cursor: pointer; transition: opacity 0.2s;
        }
        .vs-save-btn:hover { opacity: 0.9; }
        .vs-save-btn:disabled { opacity: 0.5; }
        .vs-save-outline {
          background: transparent !important;
          border: 1px solid ${isDark ? '#333' : '#D1D5DB'};
          color: ${isDark ? '#fff' : '#111'};
        }
        .vs-save-outline:hover { border-color: ${accent}; color: ${accent}; }

        /* Theme toggle */
        .vs-theme-row { display: flex; align-items: center; justify-content: space-between; }
        .vs-theme-info { display: flex; align-items: center; gap: 12px; }
        .vs-theme-label { display: block; font-size: 14px; font-weight: 500; color: ${isDark ? '#fff' : '#111'}; }
        .vs-theme-desc { display: block; font-size: 12px; color: ${isDark ? '#6B7280' : '#9CA3AF'}; }
        .vs-toggle {
          width: 44px; height: 24px; border-radius: 12px;
          background: ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'};
          border: none; position: relative; cursor: pointer; transition: background 0.2s; padding: 0;
        }
        .vs-toggle.on { background: ${accent}; }
        .vs-toggle-knob {
          display: block; width: 20px; height: 20px; border-radius: 50%;
          background: #fff; position: absolute; top: 2px; left: 2px;
          transition: transform 0.2s ease-out;
        }
        .vs-toggle.on .vs-toggle-knob { transform: translateX(20px); }

        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 640px) {
          .vs-form-grid { grid-template-columns: 1fr; }
          .vs-photo-section { flex-direction: column; text-align: center; }
          .vs-identity { align-items: center; }
        }
      `}</style>
    </div>
  );
}
